import React, { useState, useEffect } from 'react';
import { Service, Location } from '../../types';
import { useSupabaseData } from '../../hooks/useSupabaseData';
import { useEmployees } from '../../hooks/useEmployees';
import { T, rgba } from '../../utils/adminTheme';

interface AddBookingModalProps {
  onClose: () => void;
  services: Service[];
  locations: Location[];
  onBookingCreated: () => void;
  prefillDate?: string;
  prefillTime?: string;
  prefillEmployeeId?: number;
}

const fieldStyle: React.CSSProperties = {
  width: '100%',
  background: T.surface2,
  border: `1px solid ${T.line}`,
  borderRadius: T.r.md,
  padding: '8px 12px',
  fontFamily: T.body,
  fontSize: 14,
  color: T.text,
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: T.body,
  fontSize: 12,
  fontWeight: 600,
  color: T.muted,
  marginBottom: 5,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const AddBookingModal: React.FC<AddBookingModalProps> = ({ onClose, services, locations, onBookingCreated, prefillDate, prefillTime, prefillEmployeeId }) => {
  const { createBooking, customers } = useSupabaseData();
  const { employees } = useEmployees();

  const [serviceId, setServiceId] = useState<number | undefined>();
  const [locationId, setLocationId] = useState<number | undefined>();
  const [employeeId, setEmployeeId] = useState<number | undefined>(prefillEmployeeId);
  const [date, setDate] = useState(prefillDate ?? '');
  const [time, setTime] = useState(prefillTime ?? '');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Wenn ein bekannter Kunde gewählt wird, Felder befüllen
  const selectCustomer = (c: any) => {
    setName(c.name || ''); setEmail(c.email || ''); setPhone(c.phone || '');
    setCustomerSearch(c.name); setShowSuggestions(false);
  };

  // Custom Duration des gewählten Kunden für den gewählten Service
  const matchedCustomer = customers.find(c =>
    c.name?.toLowerCase() === name.toLowerCase() ||
    (c.email && c.email.toLowerCase() === email.toLowerCase())
  );
  const customDuration = serviceId && matchedCustomer?.service_durations?.[String(serviceId)]
    ? matchedCustomer.service_durations[String(serviceId)]
    : undefined;
  const selectedService = services.find(s => Number(s.id) === serviceId);
  const effectiveDuration = customDuration ?? selectedService?.duration;
  const availableEmployees = employees.filter(employee => {
    if (!employee.active) return false;
    if (!locationId) return true;
    if (!employee.location_ids?.length) return true;
    return employee.location_ids.includes(locationId);
  });
  const employeeRequired = availableEmployees.length > 0;

  const suggestions = customerSearch.length >= 2
    ? customers.filter(c => c.name?.toLowerCase().includes(customerSearch.toLowerCase())).slice(0, 5)
    : [];

  useEffect(() => {
    if (!employeeRequired) {
      setEmployeeId(undefined);
      return;
    }
    if (employeeId && availableEmployees.some(employee => employee.id === employeeId)) return;
    setEmployeeId(availableEmployees[0]?.id);
  }, [employeeId, employeeRequired, availableEmployees]);

  const isValid = serviceId && locationId && (!employeeRequired || employeeId) && date && time && name.trim();

  const handleSave = async () => {
    if (!isValid) return;
    try {
      setSaving(true);
      setError(null);
      await createBooking({
        locationId: locationId!,
        serviceId: serviceId!,
        customerName: name,
        customerEmail: email || undefined,
        customerPhone: phone || undefined,
        bookingDate: date,
        bookingTime: time,
        employeeId,
        durationOverride: effectiveDuration,
      });
      onBookingCreated();
    } catch (err) {
      console.error('Fehler beim Erstellen der Buchung:', err);
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Kunden-Suche */}
      <div style={{ position: 'relative' }}>
        <label style={labelStyle}>Kunde suchen oder Name eingeben *</label>
        <input
          type="text"
          value={customerSearch || name}
          onChange={(e) => { setCustomerSearch(e.target.value); setName(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          style={fieldStyle}
          placeholder="Name suchen oder neu eingeben…"
          autoFocus
        />
        {showSuggestions && suggestions.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.surface, border: `1px solid ${T.line}`, borderRadius: T.r.md, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 50, overflow: 'hidden' }}>
            {suggestions.map((c: any) => (
              <button key={c.id} onMouseDown={() => selectCustomer(c)} style={{ width: '100%', padding: '9px 12px', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: `1px solid ${T.lineSoft}`, cursor: 'pointer', fontFamily: T.body, fontSize: 13 }}>
                <span style={{ fontWeight: 600, color: T.text }}>{c.name}</span>
                {c.email && <span style={{ color: T.muted, marginLeft: 8, fontSize: 12 }}>{c.email}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* E-Mail + Telefon */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>E-Mail</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={fieldStyle}
            placeholder="name@beispiel.ch"
          />
        </div>
        <div>
          <label style={labelStyle}>Telefon</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={fieldStyle}
            placeholder="+41 79 000 00 00"
          />
        </div>
      </div>

      {/* Behandlung + Standort */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>Behandlung *</label>
          <select
            value={serviceId ?? ''}
            onChange={(e) => setServiceId(Number(e.target.value) || undefined)}
            style={fieldStyle}
          >
            <option value="">Bitte wählen…</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Standort *</label>
          <select
            value={locationId ?? ''}
            onChange={(e) => setLocationId(Number(e.target.value) || undefined)}
            style={fieldStyle}
          >
            <option value="">Bitte wählen…</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Mitarbeiter */}
      <div>
        <label style={labelStyle}>Mitarbeiter *</label>
        <select
          value={employeeId ?? ''}
          onChange={(e) => setEmployeeId(Number(e.target.value) || undefined)}
          style={fieldStyle}
          disabled={!employeeRequired}
        >
          {employeeRequired ? (
            availableEmployees.map((employee) => (
              <option key={employee.id} value={employee.id}>{employee.name}</option>
            ))
          ) : (
            <option value="">Kein aktiver Mitarbeiter verfügbar</option>
          )}
        </select>
        {!employeeRequired && (
          <div style={{ marginTop: 6, fontSize: 12, color: T.muted, fontFamily: T.body }}>
            Lege im Bereich Personal zuerst einen aktiven Mitarbeiter an.
          </div>
        )}
      </div>

      {/* Custom Duration Hinweis */}
      {customDuration && selectedService && (
        <div style={{ padding: '8px 12px', background: rgba(T.accent, 0.08), border: `1px solid ${rgba(T.accent, 0.25)}`, borderRadius: T.r.sm, fontSize: 12, color: T.accent, fontFamily: T.body }}>
          ⏱ Individuelle Zeit für {matchedCustomer?.name}: <strong>{customDuration} Min.</strong> (Standard: {selectedService.duration} Min.)
        </div>
      )}

      {/* Datum + Zeit */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>Datum *</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={fieldStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Uhrzeit *</label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            style={fieldStyle}
          />
        </div>
      </div>

      {error && (
        <div style={{
          background: rgba('#e53935', 0.08),
          border: `1px solid ${rgba('#e53935', 0.3)}`,
          borderRadius: T.r.md,
          padding: '10px 14px',
          color: '#c62828',
          fontFamily: T.body,
          fontSize: 13,
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
        <button
          onClick={onClose}
          disabled={saving}
          style={{
            padding: '9px 18px',
            background: T.surface2,
            border: `1px solid ${T.line}`,
            borderRadius: T.r.md,
            fontFamily: T.body,
            fontSize: 14,
            color: T.muted,
            cursor: 'pointer',
          }}
        >
          Abbrechen
        </button>
        <button
          onClick={handleSave}
          disabled={!isValid || saving}
          style={{
            padding: '9px 20px',
            background: isValid && !saving ? T.accent : T.faint,
            border: 'none',
            borderRadius: T.r.md,
            fontFamily: T.body,
            fontSize: 14,
            fontWeight: 600,
            color: isValid && !saving ? '#fff' : T.muted,
            cursor: isValid && !saving ? 'pointer' : 'not-allowed',
            transition: 'background .15s',
          }}
        >
          {saving ? 'Speichern…' : 'Buchung speichern'}
        </button>
      </div>
    </div>
  );
};

export default AddBookingModal;
